/**
 *
 * Module:        Component
 * Name:          Guru Files Sidebar
 * Description:   Renders the existing private Guru PDF and Excel files in a collapsible sidebar
 * Developers:    Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright:     Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 *
 */

import React, {useCallback, useEffect, useState} from 'react';
import {Box, CircularProgress, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Typography} from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import QeeTooltip from '../qeeTooltip';
import GuruSidebarSectionHeader from './GuruSidebarSectionHeader';
import {GURU_BODY_FONT_SIZE} from './guruTypography';

export default function GuruFilesSidebar({labels, expanded, onExpandedChange, onLoadPdfs, onLoadExcels, onDownloadPdf, onDownloadExcel}) {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [downloading, setDownloading] = useState('');
	const requestFiles = useCallback(async () => {
		const requests = [];
		if (typeof onLoadPdfs === 'function') requests.push(Promise.resolve(onLoadPdfs())
			.then(items => (Array.isArray(items) ? items : []).map(item => ({...item, fileType: 'pdf'}))));
		if (typeof onLoadExcels === 'function') requests.push(Promise.resolve(onLoadExcels())
			.then(items => (Array.isArray(items) ? items : []).map(item => ({...item, fileType: 'excel'}))));
		if (!requests.length) return [];
		const results = await Promise.allSettled(requests);
		if (results.every(result => result.status === 'rejected')) throw results[0].reason;
		return results.filter(result => result.status === 'fulfilled').flatMap(result => result.value)
			.sort((left, right) => String(right.createdAt || '').localeCompare(String(left.createdAt || '')));
	}, [onLoadExcels, onLoadPdfs]);
	useEffect(() => {
		if (!expanded) return undefined;
		let active = true;
		setLoading(true);
		setError('');
		requestFiles()
			.then(items => { if (active) setFiles(items); })
			.catch(loadError => {
				if (!active) return;
				console.error('Loading Guru files failed:', loadError);
				setError(labels.filesLoadError);
			})
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [expanded, labels.filesLoadError, requestFiles]);
	const download = async item => {
		const key = `${item.fileType}-${item.fileId || item.fileName}`;
		setDownloading(key);
		try {
			if (item.fileType === 'excel' && typeof onDownloadExcel === 'function') await onDownloadExcel(item);
			else if (item.fileType === 'pdf' && typeof onDownloadPdf === 'function') await onDownloadPdf(item);
		} finally {
			setDownloading('');
		}
	};
	if (!expanded) {
		return (
			<Box sx={{height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', pt: 1.25, bgcolor: 'background.paper'}}>
				<QeeTooltip title={labels.savedFiles}>
					<IconButton
						aria-label={labels.savedFiles}
						aria-expanded={false}
						onClick={() => onExpandedChange(true)}
						sx={{color: 'var(--primary-color, #004c3f)'}}
					>
						<FolderOutlinedIcon />
					</IconButton>
				</QeeTooltip>
			</Box>
		);
	}

	return (
		<Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper'}}>
			<GuruSidebarSectionHeader label={labels.savedFiles} expanded={expanded} onToggle={() => onExpandedChange(!expanded)} />
			{expanded && (
				<Box sx={{flex: '1 1 auto', minHeight: 0, overflowY: 'auto'}}>
					{loading && <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress size={22} /></Box>}
					{!loading && error && <Typography color="error" sx={{px: 2, py: 2}}>{error}</Typography>}
					{!loading && !error && !files.length && <Typography color="text.secondary" sx={{px: 2, py: 2}}>{labels.noFiles}</Typography>}
					{!loading && !error && Boolean(files.length) && (
						<List disablePadding>
							{files.map(item => {
								const key = `${item.fileType}-${item.sdocUnid || item.fileId || item.fileName}`;
								return (
									<QeeTooltip key={key} title={item.fileName || item.title || labels.savedFiles}>
										<ListItemButton disabled={Boolean(downloading)} onClick={() => download(item)} sx={{mx: 0.75, borderRadius: 1.5, minHeight: 42}}>
											<ListItemIcon sx={{minWidth: 34, color: item.fileType === 'excel' ? '#107c41' : '#d32f2f'}}>
												{item.fileType === 'excel' ? <GridOnOutlinedIcon fontSize="small" /> : <PictureAsPdfOutlinedIcon fontSize="small" />}
											</ListItemIcon>
											<ListItemText primary={item.fileName || item.title || labels.savedFiles} primaryTypographyProps={{noWrap: true, fontSize: GURU_BODY_FONT_SIZE, fontWeight: 400}} />
											{downloading === key && <CircularProgress size={16} />}
										</ListItemButton>
									</QeeTooltip>
								);
							})}
						</List>
					)}
				</Box>
			)}
		</Box>
	);
}
